import { Provider, ProviderRpcError } from '../../types';
import { requestChangeWalletChain } from './helper';
import { action, computed, flow, makeObservable, observable, reaction, when } from 'mobx';
import { BrowserProvider, JsonRpcSigner, type Signer } from 'ethers';
import { BaseWallet } from './BaseWallet';

function parseChainId(chainId: string | number) {
	return typeof chainId === 'number' ? chainId : Number.parseInt(chainId, chainId.startsWith('0x') ? 16 : 10);
}

export class EVMError extends Error {
	static provider(): EVMError {
		return new EVMError('Provider is undefined');
	}
}

abstract class EVMBase implements BaseWallet {
	readonly type = 'evm';
	protected listened = false;
	cachedInjectedProvider: Provider | undefined = undefined;

	accounts = observable<string>([]);
	chainId: number | undefined = undefined;

	connected: boolean = false;

	abstract loadEthereum(): Provider | undefined;

	abstract get disconnectable(): boolean;
	abstract disconnect(): Promise<void>;

	abstract readonly name: string;
	abstract readonly icon: string;
	abstract readonly info: string | undefined;
	abstract readonly website: string;

	readonly supportedChainIds: readonly number[] | undefined;

	constructor(supported: readonly number[] | undefined) {
		this.supportedChainIds = supported;
		makeObservable<EVMBase, 'handleAccounts' | 'handleConnect' | 'handleChain' | 'handleDisconnect'>(this, {
			chainId: observable,
			accounts: false,
			connected: observable,

			signer: computed,

			cachedInjectedProvider: observable.ref,
			provider: computed({ keepAlive: true }),
			injectedProvider: computed,

			disconnectable: computed,
			selectedAddress: computed,
			isReady: computed,
			isInstalled: computed,
			isMetamask: computed,
			loading: computed,

			loadEthereum: action.bound,
			ensureInjectedProvider: action.bound,

			handleConnect: action.bound,
			handleDisconnect: action.bound,
			handleAccounts: action.bound,
			handleChain: action.bound,
		});

		reaction(() => this.injectedProvider, this.fixListeners.bind(this));
		when(
			() => true,
			() => this.fixListeners(this.injectedProvider, undefined),
		);
	}

	isEVM(): this is EVMBase {
		return true;
	}

	get isMetamask() {
		if (!this.injectedProvider) return false;
		const provider = this.injectedProvider as unknown as object;
		if ('isMetaMask' in provider) {
			return provider.isMetaMask as boolean;
		}
		return false;
	}

	get isInstalled() {
		return Boolean(this.injectedProvider);
	}

	get isReady() {
		return Boolean(this.chainId && this.selectedAddress && this.injectedProvider);
	}

	get loading() {
		return !this.isReady;
	}

	ensureInjectedProvider() {
		this.cachedInjectedProvider ??= this.loadEthereum();
	}

	get injectedProvider() {
		if (typeof window === 'undefined') {
			return null;
		}

		this.ensureInjectedProvider();

		return this.cachedInjectedProvider;
	}

	get provider(): BrowserProvider | undefined {
		// Touch chainId to re-trigger when it changes
		this.chainId;
		if (this.injectedProvider) {
			return new BrowserProvider(this.injectedProvider as any, this.chainId);
		}
		return;
	}

	get selectedAddress(): string | undefined {
		return this.accounts[0];
	}

	get signer(): JsonRpcSigner | undefined {
		if (!this.provider || !this.selectedAddress) return;
		return new JsonRpcSigner(this.provider, this.selectedAddress);
	}

	protected _isConnected() {
		if (!this.injectedProvider) return false;
		const provider = this.injectedProvider as unknown;
		if (provider && typeof provider === 'object' && 'isConnected' in provider) {
			switch (typeof provider.isConnected) {
				case 'boolean':
					return provider.isConnected;
				case 'function':
					return provider.isConnected() as boolean;
			}
		}
		return false;
	}

	supports(chain: Readonly<Chain> | Chain | number): boolean {
		const isChain = typeof chain !== 'number';
		const chainId = isChain ? chain.chainId : chain;
		if (chainId === 0) return false;
		if (isChain && chain.nameId === 'hypercore') return this.supports(42161);
		if (!this.supportedChainIds) return true;
		return this.supportedChainIds.includes(chainId);
	}

	reset = action(() => {
		this.chainId = undefined;
		this.accounts.clear();
	});

	fetchAccounts = flow(function* fetchAccounts(this: EVMBase) {
		const accounts: string[] = yield this.injectedProvider!.request({
			method: 'eth_accounts',
			params: [],
		});
		if (!accounts) {
			throw new Error('Invalid data');
		}
		this.accounts.replace(accounts);
	});

	fetchChainId = flow(function* (this: EVMBase) {
		const payload = {
			method: 'eth_chainId',
			params: [],
			jsonrpc: '2.0',
		};
		const chainId = yield this.injectedProvider!.request(payload);
		this.chainId = chainId != null ? Number(chainId) : undefined;
	});

	refresh = flow(function* refresh(this: EVMBase) {
		try {
			if (!this.injectedProvider) throw EVMError.provider();
			this.connected = this._isConnected();
			if (this.connected) {
				yield Promise.all([this.fetchChainId(), this.fetchAccounts()]);
			}
		} catch (e) {
			this.reset();
			if (e && typeof e === 'object' && 'code' in e) {
				if (e.code === 4001) {
					console.error('The user rejected the request.');
				}
			}
			throw e;
		}
	});

	protected fixListeners(provider: Provider | undefined | null, previousProvider: Provider | undefined | null) {
		if (previousProvider) {
			this.removeListeners(previousProvider);
		}
		if (provider) {
			this.addListeners(provider);
		}
	}

	protected addListeners(provider: Provider) {
		if (this.listened) return;
		if (!provider) {
			throw EVMError.provider();
		}
		provider.on('connect', this.handleConnect);
		provider.on('disconnect', this.handleDisconnect);
		provider.on('chainChanged', this.handleChain);
		provider.on('accountsChanged', this.handleAccounts);
		this.listened = true;
	}

	protected removeListeners(provider: Provider) {
		if (!this.listened) return;
		if (!provider) {
			throw EVMError.provider();
		}
		provider.removeListener('connect', this.handleConnect);
		provider.removeListener('disconnect', this.handleDisconnect);
		provider.removeListener('chainChanged', this.handleChain);
		provider.removeListener('accountsChanged', this.handleAccounts);
		this.listened = false;
	}

	connect = flow(function* enable(this: EVMBase, quick = false) {
		const provider = this.injectedProvider;
		if (!provider) {
			throw EVMError.provider();
		}
		if (!(this.isReady && quick)) {
			yield provider.request({ method: 'eth_requestAccounts' });
			yield this.fetchAccounts();
			yield this.fetchChainId();
			this.connected = true;
		} else {
			yield this.refresh();
			if (this.chainId && this.selectedAddress) {
				this.connected = true;
			}
		}
	});

	disable = action(function disable(this: { removeListeners: () => void }) {
		this.removeListeners();
	});

	async changeWalletChain(chain: Chain) {
		if (!this.injectedProvider) {
			throw EVMError.provider();
		}
		await requestChangeWalletChain(chain, this.injectedProvider);
		await this.fetchChainId();
	}

	/* Event Handlers */
	protected handleConnect({ chainId }: { chainId: string }) {
		this.chainId = parseChainId(chainId);
		this.connected = true;
	}
	protected handleDisconnect(_error: ProviderRpcError) {
		this.reset();
		this.connected = false;
	}
	protected handleAccounts(accounts: string[]) {
		if (accounts.length === 0) {
			this.reset();
		} else {
			this.fetchAccounts();
			if (this.chainId == undefined) {
				this.fetchChainId();
			}
		}
	}
	protected handleChain(chainId: string | number) {
		this.chainId = parseChainId(chainId);
		this.fetchAccounts();
	}
}

export default EVMBase;